import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
  Chip,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
} from '@mui/material';
import FarmIcon from '@mui/icons-material/Agriculture';
import StoreIcon from '@mui/icons-material/Store';
import LogisticsIcon from '@mui/icons-material/LocalShipping';
import CarbonIcon from '@mui/icons-material/Forest';
import CheckIcon from '@mui/icons-material/CheckCircle';
import CouponIcon from '@mui/icons-material/LocalOffer';
import TrialIcon from '@mui/icons-material/Timer';
import { useUser } from '../../context/UserContext';
import { NotificationService } from '../../services/errorService';

const SubscriptionManagement: React.FC = () => {
  const { personalInfo, updatePersonalInfo, hasModule } = useUser();
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);

  const modules = [
    {
      id: 'farm_management',
      name: 'Farm Management Suite',
      description: 'Calculator, field management, and smart farming tools.',
      price: 2500,
      icon: <FarmIcon color="success" />,
      color: 'success.main'
    },
    {
      id: 'marketplace_seller',
      name: 'Seller Pro Hub',
      description: 'Open shops, manage products, B2B Deal Room, and wealth tracking.',
      price: 3500,
      icon: <StoreIcon color="warning" />,
      color: 'warning.main'
    },
    {
      id: 'logistics',
      name: 'Supply Chain Logistics',
      description: 'Smart delivery booking, route optimization, and fleet tracking.',
      price: 1500,
      icon: <LogisticsIcon color="info" />,
      color: 'info.main'
    },
    {
      id: 'carbon_credits',
      name: 'Carbon Credit Monetization',
      description: 'Track sequestration, water saving, and sell carbon credits.',
      price: 5000,
      icon: <CarbonIcon color="success" />,
      color: 'success.dark'
    }
  ];

  const handleApplyCoupon = () => {
    if (couponCode.toUpperCase() === 'FARMERS20') {
      setDiscount(0.2);
      NotificationService.success('Success', '20% discount applied!');
    } else {
      NotificationService.error('Error', 'Invalid coupon code.');
    }
  };

  const handleToggleModule = async (moduleId: string) => {
    let newModules = [...personalInfo.registeredModules];
    if (newModules.includes(moduleId)) {
      newModules = newModules.filter(m => m !== moduleId);
    } else {
      newModules.push(moduleId);
    }
    
    try {
      await updatePersonalInfo({ registeredModules: newModules });
      NotificationService.success('Success', 'Access updated successfully.');
    } catch (error) {
      NotificationService.error('Error', 'Failed to update access.');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">Access & Subscriptions</Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your platform modules and unlock elite agricultural tools.
        </Typography>
      </Box>

      {personalInfo.subscriptionStatus === 'free_trial' && (
        <Alert icon={<TrialIcon />} severity="info" sx={{ mb: 4, borderRadius: '12px' }}>
          You are currently on a <strong>14-day free trial</strong>. Unlock all features permanently below.
        </Alert>
      )}

      <Grid container spacing={3}>
        {modules.map((mod) => {
          const isRegistered = hasModule(mod.id);
          return (
            <Grid item xs={12} md={6} key={mod.id}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderRadius: '16px', 
                  border: isRegistered ? '2px solid' : '1px solid',
                  borderColor: isRegistered ? mod.color : 'divider',
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: '12px' }}>
                      {mod.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" fontWeight="bold">{mod.name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {mod.description}
                      </Typography>
                      <Typography variant="h6" color="primary.main" fontWeight="bold">
                        KSh {mod.price.toLocaleString()} / month
                      </Typography>
                    </Box>
                    {isRegistered && <CheckIcon color="success" />}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Button 
                    fullWidth 
                    variant={isRegistered ? 'outlined' : 'contained'} 
                    color={isRegistered ? 'error' : 'primary'}
                    onClick={() => handleToggleModule(mod.id)}
                    sx={{ borderRadius: '10px', py: 1 }}
                  >
                    {isRegistered ? 'Deactivate Access' : 'Subscribe & Activate'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ mt: 4, p: 3, borderRadius: '16px' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>Promotions & Billing</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Coupon Code"
            variant="outlined"
            size="small"
            fullWidth
            value={couponCode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCouponCode(e.target.value)}
            placeholder="e.g. FARMERS20"
          />
          <Button 
            variant="contained" 
            startIcon={<CouponIcon />}
            onClick={handleApplyCoupon}
            sx={{ px: 4, borderRadius: '8px' }}
          >
            Apply
          </Button>
        </Stack>
        {discount > 0 && (
          <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}>
            Promotional discount of {(discount * 100)}% will be applied to your next bill.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default SubscriptionManagement;
